import { useEffect, useState } from "react";
import { IBaseInputProps } from "../Inputs";
import { fieldId } from "../utils";
import { useAtomValue } from "jotai";
import { hostDataAtom, useModifyUploadList } from "../../../atoms";
import { isVideo } from "../../Upload";
import { getStillVideoFrameFromUrl, uploadFile } from "./Utils";
import { tMsg } from "../../ToastNotifications";
import { mergeClass } from "../../../utils";
import { Spinner } from "../../Loader";
import _ from "lodash";
import { format } from "date-fns";

const object_names = ["object_mtl", "object_obj", "object_texture"];

export const FileUpload = (
  props: IBaseInputProps & { nothumb?: boolean }
): JSX.Element => {
  const { name, onChange, value, accept, required, disabled, nothumb, type } =
    props;

  const selectorId = `file_select_${name}`;
  const getFieldId = fieldId(name);

  const [file, setFile] = useState<File | null>(null);
  const [imgUrl, setImgUrl] = useState<string>(_.isString(value) ? value : "");
  const [uploadText, setUploadText] = useState<string>("No file selected");
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const hostData = useAtomValue(hostDataAtom);
  const [addToList, removeFromList] = useModifyUploadList(getFieldId);

  // Update upload preview from remote source on element load
  useEffect(() => {
    if (imgUrl !== "") {
      if (isVideo(imgUrl)) {
        getStillVideoFrameFromUrl(imgUrl).then((imageDataUrl) => {
          setUploadText(imgUrl);
          setImgUrl(imageDataUrl);
        });
      } else {
        // const urlParts = imgUrl.split("/");
        setUploadText(imgUrl);
        setImgUrl(imgUrl);
      }
    }
    return () => removeFromList();
  }, []);

  // Update upload preview from local source on `file` change
  useEffect(() => {
    if (file) {
      setFile(file);

      // Set image preview from local source
      if (isVideo(file.name)) {
        const url = URL.createObjectURL(file);
        getStillVideoFrameFromUrl(url).then((imageDataUrl) => {
          setUploadText(`${file.name} - Uploading`);
          setImgUrl(imageDataUrl);
        });
      } else {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onloadend = () => {
          setUploadText(file.name);
          if (reader.result) {
            setUploadText(`${file.name} - Uploading`);
            setImgUrl(reader.result.toString());
          }
        };
      }
    }
  }, [file]);

  const fileSelected = (selectedFile: File | null) => {
    if (selectedFile !== null) {
      const newFilename = _.includes(object_names, name)
        ? selectedFile.name
        : `${format(new Date(), "yyyyMMddHHmmss")}_${selectedFile.name}`;
      console.log(newFilename);
      const stampedFile = new File([selectedFile], newFilename);
      setFile(stampedFile);
      startFileUpload(stampedFile); // don't handle on button click anymore
    } else {
      setFile(null);
    }
  };

  const startFileUpload = async (selectedFile: File | null) => {
    if (selectedFile === null) {
      tMsg.error("Unable to upload selected file");
      return;
    }
    addToList();
    console.log("hostData:", hostData);
    const location = await uploadFile(
      selectedFile,
      onUploadFinished,
      updateProgress,
      hostData.aws_bucket,
      hostData.aws_region
    );
    console.log("uploaded file to:", location);
    setUploadText(`Uploading ${selectedFile.name}`);
    setIsUploading(true);
  };

  const onUploadFinished = (fileLocation: string) => {
    setImgUrl(fileLocation);
    const obj = { target: { value: fileLocation } };
    onChange(obj);
    setUploadText(fileLocation);
    setFile(null);
    setIsUploading(false);
    removeFromList();
  };

  const updateProgress = (amount: number) => {
    setUploadText(`Uploading ${file?.name} - ${amount}%`);
  };

  return (
    <div className="w-full flex">
      <input
        type="file"
        onChange={(e) =>
          fileSelected(e.target.files ? e.target.files[0] : null)
        }
        className={mergeClass("w-full")}
        accept={accept}
        id={selectorId}
        data-has-url={value !== ""}
        data-file-url={value}
        data-is-direct
        hidden
      />
      <input
        id={getFieldId}
        name={name}
        value={value}
        type="hidden"
        onChange={(e) => e.preventDefault()}
        required={required}
      />
      {nothumb || (
        <div
          id="thumb_container"
          className="w-12 h-auto bg-gray-300 overflow-hidden"
        >
          <img
            src={imgUrl || "/assets/default-image.jpg"}
            className={mergeClass(`object-cover w-full h-full`, {
              "opacity-40": !value || file !== null,
            })}
          />
        </div>
      )}
      <div className="flex-grow border border-[#ccc] grid content-center px-2.5 text-sm">
        <div className="flex gap-3">
          <span
            className={mergeClass("break-words", {
              "opacity-50": file !== null || !value || disabled,
            })}
          >
            {uploadText}
          </span>
          {isUploading && <Spinner />}
        </div>
      </div>
      <label
        htmlFor={selectorId}
        className={mergeClass(
          "px-5 py-2 grid bg-primaryColor border-primaryColor border text-white font-medium",
          "transition hover:bg-white hover:text-primaryColor hover:border-primaryColor cursor-pointer",
          { "opacity-50 pointer-events-none": disabled }
        )}
      >
        {!imgUrl && !file ? "Select file" : "Change file"}
      </label>
    </div>
  );
};
